/**
 * T-SQL engines don't speak ANSI `LIMIT` — SQL Server and its cloud-hosted
 * variants only understand `SELECT TOP n ...` for capping a preview to 100
 * rows. Anything landing in this set gets that alternate phrasing instead of
 * the default `LIMIT` tail.
 *
 * NetSuite JDBC (`netsuite_jdbc`) belongs here too, for a reason worth
 * spelling out rather than assuming: this UI doesn't talk to NetSuite
 * directly, it goes through SuiteAnalytics Connect, NetSuite's JDBC/ODBC
 * driver, which is built on a third-party SQL-translation layer called the
 * OpenAccess SDK SQL Engine (that name shows up directly in NetSuite's own
 * error messages, which is how it got identified). That engine follows the
 * T-SQL family and expects `SELECT TOP n ...`, per NetSuite's own SuiteQL
 * syntax documentation — it has nothing to do with Oracle's own database
 * engine, even though NetSuite itself has been an Oracle-owned product since
 * 2016. A first attempt here assumed the opposite, that ownership implied
 * Oracle's `FETCH FIRST` syntax; a live test against a real `netsuite_jdbc`
 * credential rejected that with a syntax error before it ever shipped, and a
 * follow-up live test confirmed `TOP` is what this engine actually expects.
 *
 * `teradata` also belongs here: Teradata's own SQL Data Manipulation
 * Language docs are explicit that its row-limiting mechanism is `TOP`, not
 * `LIMIT` — `LIMIT` isn't part of Teradata SQL at all. This one is still
 * only documentation-confirmed, not live-tested against a real Teradata
 * credential yet (unlike Oracle and NetSuite above); the trailing `;` this
 * builder keeps is a best-guess default carried over from the SQL Server
 * treatment, not something proven safe for Teradata specifically — treat it
 * as the next thing to verify live before relying on it.
 *
 * `sybase` belongs here too, and for a very literal reason: Sybase ASE added
 * `TOP n` in version 12.5.3 explicitly for compatibility with Microsoft SQL
 * Server (stated directly in Sybase's own documentation), since Sybase and
 * SQL Server share the same Transact-SQL ancestry — Microsoft's original SQL
 * Server codebase was licensed from Sybase in the late 1980s. Like Teradata,
 * this is documentation-confirmed (`TOP` is correct, `LIMIT` doesn't exist in
 * Sybase SQL) but not yet live-tested, and the trailing `;` is the same
 * carried-over assumption from the SQL Server treatment.
 */
const TOP_SYNTAX_CONNECTIONS = new Set([
  'sqlserver',
  'cloudsql_sqlserver',
  'azure_synapse',
  'netsuite_jdbc',
  'teradata',
  'sybase',
]);

/**
 * Connections where a table is really addressed as `database.table`, so the
 * auto-generated preview needs that prefix to point at the right table.
 * Everyone else is left unqualified because their own connection already
 * resolves to a single default database or schema. Snowflake, SQL Server,
 * and Sybase are the odd ones out here too — their object names are really
 * `database.owner.table`, three parts, not two, so a bare `database.table`
 * prefix would be misread as `owner.table` in the current database rather
 * than pointing at a different one — and schema-qualified engines like
 * Postgres, Redshift, and Oracle would actually break if we prefixed them
 * with a database, since for them the qualifier that matters is the schema,
 * not the database.
 *
 * BigQuery (`bigquery`) belongs here, and it breaks the "connection already
 * resolves a default" assumption in a way none of the others do. What this
 * UI browses as a "database" for BigQuery is really a *dataset* —
 * `backend-connectors`' `BigQueryConnectorService` lists datasets at the top
 * level of the probe tree — and a BigQuery credential pins only a project,
 * so there is no connection-level default dataset for a bare table name to
 * resolve against. Nothing downstream supplies one either: both the sample
 * path (`BigQueryConnectorService.readSample`) and the ingestion path
 * (`BigQuerySourceTask.saveQueryToTableOpt`) hand query-mode SQL to
 * `QueryJobConfiguration.newBuilder(query)` verbatim, with no
 * `setDefaultDataset(...)`, so Google rejects the unqualified reference
 * outright: `Table "x" must be qualified with a dataset (e.g.
 * dataset.table).` The two-part `dataset.table` form is exactly what
 * BigQuery expects — the project half comes from the job's credential — and
 * this was confirmed live against a real BigQuery credential: the same
 * generated query fails unqualified and succeeds dataset-qualified. One
 * known limit, shared with every engine in this set since none of them
 * quote the reference: a table using BigQuery's "flexible" naming (dashes,
 * spaces) would additionally need backtick quoting to run — dataset IDs
 * themselves can't contain those characters.
 */
const DATABASE_QUALIFIED_CONNECTIONS = new Set([
  'mysql',
  'cloudsql_mysql',
  'hive',
  'aws_athena',
  'teradata',
  'bigquery',
]);

type ConnectionSqlProfile = {
  qualifyWithDatabase: boolean;
  buildQuery: (ref: string) => string;
};

const limitSyntax: ConnectionSqlProfile['buildQuery'] = (ref) =>
  `SELECT *\nFROM ${ref}\nLIMIT 100;`;
const topSyntax: ConnectionSqlProfile['buildQuery'] = (ref) => `SELECT TOP 100 *\nFROM ${ref};`;

/**
 * Oracle is the one dialect here that can't fall back to a shared syntax:
 * it doesn't understand `LIMIT` at all and needs the ANSI SQL:2008
 * `FETCH FIRST ... ROWS ONLY` clause instead. It also has an unrelated quirk
 * worth calling out, because it's easy to miss — a trailing `;` is fine when
 * you paste a query into SQL*Plus or SQL Developer (they quietly strip it
 * before the statement ever reaches the database), but Oracle's JDBC driver
 * runs the string exactly as it's given, semicolon and all, and rejects it
 * with `ORA-00933`. This was confirmed live against a real oracle_autonomous
 * credential: the same query fails with the trailing `;` and succeeds without it.
 * So, unlike every other branch below, we deliberately leave the semicolon off.
 *
 * `db2` and `as400` share this exact profile, and for a good reason: Db2 is
 * where `FETCH FIRST n ROWS ONLY` originated — Oracle adopted the same ANSI
 * SQL:2008 clause later, in 12c. Db2 for i (the engine behind an `as400`
 * connection) is the same Db2 SQL family and documents the identical clause.
 * Neither platform's own docs treat `LIMIT` as a real clause. Qualification
 * is also deliberately left off for both, the same way it's left off for
 * Oracle: `backend-connectors`' `AS400Dialect` explicitly reports its
 * namespace term as `schema`, the same override `OracleDialect` and
 * `PostgreSqlDialect` make, and that value is what decides whether the
 * "database" this UI browses for a connection actually comes from the JDBC
 * driver's schema listing instead of its catalog listing — so what looks
 * like a `database` in this UI is already a schema underneath, and Db2's own
 * two-part addressing is `schema.table`, not `database.table`. `Db2Dialect`
 * doesn't make that same override (it inherits the default), but Db2 LUW's
 * own SQL reference is just as clear that a two-part `database.table` isn't
 * real syntax there either — a 3-part name is tolerated only when the
 * database segment matches the one already connected to, and true
 * cross-database access needs federation, not a plain prefix. Treating `db2`
 * the same as `as400` here matches how Db2 SQL actually works, even though
 * `Db2Dialect` alone wouldn't tell you that. The trailing semicolon has an
 * actual reproduction for `as400` — a `kafka-connect-jdbc` issue shows
 * `SELECT CURRENT_TIMESTAMP;` failing against Db2 for AS/400 over JDBC with
 * `SQL0104` ("invalid token ;"), the same failure class as Oracle's
 * `ORA-00933` — so leaving it off here is confirmed, not just inherited.
 */
const fetchFirstSyntax: ConnectionSqlProfile['buildQuery'] = (ref) =>
  `SELECT *\nFROM ${ref}\nFETCH FIRST 100 ROWS ONLY`;

/**
 * Looks up how a given connection type wants its preview query shaped: which
 * row-limiting syntax it understands, and whether the table reference needs
 * a database prefix. Oracle, Oracle Autonomous, Db2, and Db2 for i (AS/400)
 * get their own explicit cases because they share a `FETCH FIRST`/unqualified
 * profile that neither the `TOP` nor `LIMIT` treatment below can express;
 * every other connection type falls through to `default`, where the two Sets
 * above do the actual sorting — so adding a brand-new connection that behaves
 * like an existing dialect never requires touching this function at all.
 */
export function getConnectionProfile(connectionType?: string | null): ConnectionSqlProfile {
  switch (connectionType) {
    case 'oracle':
    case 'oracle_autonomous':
    case 'db2':
    case 'as400':
      return { qualifyWithDatabase: false, buildQuery: fetchFirstSyntax };

    default:
      return {
        qualifyWithDatabase: DATABASE_QUALIFIED_CONNECTIONS.has(connectionType ?? ''),
        buildQuery: TOP_SYNTAX_CONNECTIONS.has(connectionType ?? '') ? topSyntax : limitSyntax,
      };
  }
}

/**
 * Builds the auto-generated preview query for whichever table is currently
 * selected, so the user sees a sensible `SELECT` before typing anything of
 * their own. Returns an empty string when there's no table yet, so callers
 * can treat "nothing selected" as simply "nothing to show" instead of
 * special-casing it themselves.
 */
export function buildAutoSelectSql(
  table?: string | null,
  database?: string | null,
  connectionType?: string | null,
): string {
  if (!table) return '';
  const profile = getConnectionProfile(connectionType);
  const ref = database && profile.qualifyWithDatabase ? `${database}.${table}` : table;
  return profile.buildQuery(ref);
}
